/**
 * Payment Validation Helper Functions
 *
 * Pure functions for validating payment form inputs.
 * Separated for easier testing and maintainability.
 */

import { UserResponseAgentAccountCountryEnum } from '../../openapi/yenta';
import { CA_POSTAL_CODE, US_POSTAL_CODE } from '../../utils/RegexUtils';

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface StripeElementsCompletionState {
  cardNumberComplete: boolean;
  cardExpiryComplete: boolean;
  cardCvcComplete: boolean;
}

/**
 * Validates that all Stripe card elements are complete
 */
export const validateStripeElements = (
  state: StripeElementsCompletionState,
): ValidationResult => {
  const errors: string[] = [];

  if (!state.cardNumberComplete) {
    errors.push('Card number is incomplete');
  }

  if (!state.cardExpiryComplete) {
    errors.push('Expiry date is incomplete');
  }

  if (!state.cardCvcComplete) {
    errors.push('Security code is incomplete');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Builds a user-friendly error message from validation errors
 */
export const buildValidationErrorMessage = (errors: string[]): string => {
  if (errors.length === 0) {
    return '';
  }

  if (errors.length === 1) {
    return errors[0];
  }

  return 'Please complete all required fields above.';
};

/**
 * Gets the appropriate error message for a specific field
 */
export const getFieldErrorMessage = (
  fieldName: 'cardNumber' | 'cardExpiry' | 'cardCvc',
  isComplete: boolean,
): string | undefined => {
  if (isComplete) {
    return undefined;
  }

  const errorMessages = {
    cardNumber: 'Your card number is incomplete.',
    cardExpiry: "Your card's expiration date is incomplete.",
    cardCvc: "Your card's security code is incomplete.",
  };

  return errorMessages[fieldName];
};

/**
 * Checks if payment is required based on subtotal and minimum threshold
 */
export const checkPaymentRequired = (
  subtotal: number,
  minThreshold: number,
): boolean => {
  return subtotal >= minThreshold;
};

/**
 * Determines country based on currency code
 * @param currency - Currency code (e.g., 'USD', 'CAD')
 * @returns Country enum or undefined if currency is not recognized
 */
export const getCountryFromCurrency = (
  currency?: string,
): UserResponseAgentAccountCountryEnum | undefined => {
  if (!currency) {
    return undefined;
  }

  const currencyToCountryMap: Record<
    string,
    UserResponseAgentAccountCountryEnum
  > = {
    USD: UserResponseAgentAccountCountryEnum.UnitedStates,
    CAD: UserResponseAgentAccountCountryEnum.Canada,
  };

  return currencyToCountryMap[currency.toUpperCase()];
};

/**
 * Checks if user is from Canada
 * Following bolt pattern from AgentHelper.ts
 */
export const isCanadianUser = (
  country?: UserResponseAgentAccountCountryEnum,
): boolean => country === UserResponseAgentAccountCountryEnum.Canada;

/**
 * Validates zip code or postal code based on user's country
 * US: 12345 or 12345-6789
 * Canada: A1A 1A1 (with space required)
 *
 * @param value - The zip/postal code to validate
 * @param country - User's account country
 * @returns true if valid, error message string if invalid
 */
export const validateZipOrPostalCode = (
  value: string,
  country?: UserResponseAgentAccountCountryEnum,
): string | true => {
  const trimmed = value.trim();

  // Canadian users must use Canadian postal code format
  if (isCanadianUser(country)) {
    if (CA_POSTAL_CODE.test(trimmed)) {
      return true;
    }
    return 'Please enter a valid Canadian postal code (e.g., A1A 1A1)';
  }

  // US users (or unknown country) must use US zip code format
  if (US_POSTAL_CODE.test(trimmed)) {
    return true;
  }
  return 'Please enter a valid US zip code (e.g., 12345 or 12345-6789)';
};
